import { useCallback, useEffect, useState } from 'react';

import { compareBoardItems } from '../lib/boardItems';
import db, { DEXIE_IS_LOADING } from '../lib/db';
import { WIDGET_BOARD_RESOURCE, WIDGET_BOARD_SCHEMA_VERSION, ensureWorkspaceWidgetBoard } from '../lib/workspaceWidgetBoardStore';

import useKeyedLiveQuery from './useKeyedLiveQuery';

const EMPTY_ITEMS = Object.freeze([]);

export function useWidgetBoardSnapshot(workspaceId, { requiresSnapshot = true } = {}) {
  const [error, setError] = useState(null);
  const queryKey = workspaceId == null ? null : String(workspaceId);
  const { loaded, value } = useKeyedLiveQuery(queryKey, async () => {
    const coverage = await db._resource_coverage.get([queryKey, WIDGET_BOARD_RESOURCE]);
    const boardItems = await db.board_items.where('[workspace_id+boardable_type]').equals([queryKey, 'widget']).toArray();
    const widgetIds = boardItems.map((item) => item.boardable_id).filter(Boolean);
    const widgets = widgetIds.length
      ? await db.widgets
          .where('[workspace_id+id]')
          .anyOf(widgetIds.map((widgetId) => [queryKey, widgetId]))
          .toArray()
      : [];
    const widgetsById = new Map(widgets.map((widget) => [String(widget.id), widget]));
    const serverBackedWidgetIds = new Set(
      boardItems
        .filter((item) => !String(item.id).startsWith('optimistic:'))
        .map((item) => item.boardable_id)
        .filter(Boolean)
        .map(String),
    );
    const items = boardItems
      .filter((item) => !String(item.id).startsWith('optimistic:') || !serverBackedWidgetIds.has(String(item.boardable_id)))
      .map((item) => ({ ...item, boardable: widgetsById.get(String(item.boardable_id)) || null }))
      .sort(compareBoardItems);

    return { coverage, items };
  });
  const result = loaded ? value : DEXIE_IS_LOADING;

  const coverage = result === DEXIE_IS_LOADING ? null : result?.coverage;
  const hasReadyData = coverage?.status === 'ready' && coverage.schema_version === WIDGET_BOARD_SCHEMA_VERSION;
  const accessEpoch = Number(coverage?.access_epoch || 0);

  useEffect(() => {
    if (!queryKey || !requiresSnapshot || hasReadyData) return undefined;

    let cancelled = false;
    setError(null);
    ensureWorkspaceWidgetBoard(queryKey).catch((fetchError) => {
      if (!cancelled) setError(fetchError);
    });
    return () => {
      cancelled = true;
    };
  }, [accessEpoch, hasReadyData, queryKey, requiresSnapshot]);

  const retry = useCallback(() => {
    if (!queryKey || !requiresSnapshot) return Promise.resolve(false);
    setError(null);
    return ensureWorkspaceWidgetBoard(queryKey, { force: true }).catch((fetchError) => {
      setError(fetchError);
      return false;
    });
  }, [queryKey, requiresSnapshot]);

  if (result === DEXIE_IS_LOADING || (requiresSnapshot && !hasReadyData)) {
    return { status: error ? 'error' : 'loading', error, items: EMPTY_ITEMS, retry };
  }

  return { status: 'ready', error, items: result.items || EMPTY_ITEMS, retry };
}
